import React from 'react';
import { useMutation } from 'react-query';
import { Trans, useTranslation } from 'react-i18next';
import { useRequest } from '@datahub/api-client';
import { ApiOperation } from '../../../../../api';
import { UpdateAutoSendInvoiceToOCRPayloadInterface } from './interfaces/UpdateAutoSendInvoiceToOCRPayload.interface';
import { UpdateUploadHubSettingsMutationPropsInterface } from './interfaces/UpdateUploadHubSettingsMutationProps.interface';
import useGeneralErrorHandler from '../../../../error-handlers/useGeneralErrorHandler';
import useOnError from '../../../../error-handlers/useOnError';
import { queryClient } from '../../../../../queryClient';
import { getUploadHubSettingsQueryKey } from '../../../../queries/queryKeys';
import useDebouncedMutation from '../../../useDebounced.mutation';
import { useAlert } from '../../../../../alert/Alert.provider';

const useUpdateAutoSendInvoiceToOCRMutation = ({
  setOtherError,
}: UpdateUploadHubSettingsMutationPropsInterface = {}) => {
  const { t } = useTranslation();
  const { showAlert } = useAlert();

  const { request } = useRequest(ApiOperation.UpdateAutoSendInvoiceToOCR);

  const generalErrorHandler = useGeneralErrorHandler(setOtherError);
  const { onError } = useOnError([], generalErrorHandler);

  const { mutate, ...rest } = useMutation(
    async (payload: UpdateAutoSendInvoiceToOCRPayloadInterface) => {
      const res = await request({ data: { ...payload } });

      return {
        data: res.data,
        payload,
      };
    },
    {
      onSuccess: (
        _,
        { automaticallySendInvoiceToOcr }: UpdateAutoSendInvoiceToOCRPayloadInterface,
      ) => {
        queryClient.invalidateQueries(getUploadHubSettingsQueryKey);

        showAlert(
          <Trans
            i18nKey="pluginSettings.sections.uploadHub.subsections.general.controls.autoSendInvoicesToOCR.success"
            values={{
              status: automaticallySendInvoiceToOcr ? t('global.enabled') : t('global.disabled'),
            }}
            components={{ bold: <strong /> }}
          />,
          automaticallySendInvoiceToOcr ? 'success' : 'info',
        );
      },
      onError,
    },
  );

  const { mutateDebounced } =
    useDebouncedMutation<UpdateAutoSendInvoiceToOCRPayloadInterface>(mutate);

  return {
    mutate,
    mutateDebounced,
    ...rest,
  };
};

export default useUpdateAutoSendInvoiceToOCRMutation;
